import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useUpgradeUserToCreatorMutation } from "../slices/usersApiSlice";
import { setCredentials } from "../slices/authSlice";
import { useGlobal } from "../contexts/globalContext";
import {
  useConfirmStripeUpgradePaymentMutation,
  useCreateStripeUpgradePaymentIntentMutation,
  useGetStripeConfigQuery,
} from "../slices/stripeApiSlice";
import Loader from "../components/Loader";

const StripeUpgradeForm = ({
  creatorData,
  onUpgraded,
  createUpgradeIntent,
  confirmUpgradePayment,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardholderName, setCardholderName] = useState("");

  const elementBaseClass =
    "rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-800";
  const stripeFieldOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#1e293b",
        "::placeholder": {
          color: "#94a3b8",
        },
      },
      invalid: {
        color: "#dc2626",
      },
    },
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    const card = elements.getElement(CardNumberElement);
    if (!card) return;

    try {
      setProcessing(true);
      const intent = await createUpgradeIntent().unwrap();
      const { error, paymentIntent } = await stripe.confirmCardPayment(intent.clientSecret, {
        payment_method: {
          card,
          billing_details: {
            name: cardholderName || creatorData?.name || undefined,
          },
        },
      });

      if (error) throw new Error(error.message || "Stripe payment failed");
      if (!paymentIntent || paymentIntent.status !== "succeeded") {
        throw new Error("Stripe payment was not completed");
      }

      await confirmUpgradePayment(paymentIntent.id).unwrap();
      await onUpgraded(creatorData);
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Stripe payment failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={submitHandler} className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Cardholder Name</label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Name on card"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
        />
      </div>
      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Card Number</label>
        <div className={elementBaseClass}>
          <CardNumberElement options={stripeFieldOptions} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Expiry Date</label>
          <div className={elementBaseClass}>
            <CardExpiryElement options={stripeFieldOptions} />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">CVC</label>
          <div className={elementBaseClass}>
            <CardCvcElement options={stripeFieldOptions} />
          </div>
        </div>
      </div>
      <button
        type="submit"
        disabled={!stripe || processing}
        className="inline-flex items-center justify-center rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {processing ? "Processing..." : "Pay with Stripe"}
      </button>
    </form>
  );
};

export default function BecomeCreatorScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const membershipFee = Number(process.env.REACT_APP_CREATOR_MEMBERSHIP_FEE || 24.99);
  const [upgradeUserToCreator, { isLoading, error }] = useUpgradeUserToCreatorMutation();
  const [createUpgradeIntent] = useCreateStripeUpgradePaymentIntentMutation();
  const [confirmUpgradePayment] = useConfirmStripeUpgradePaymentMutation();
  const { data: stripeConfig } = useGetStripeConfigQuery();

  const { setHasBecome } = useGlobal();
  const [showForm, setShowForm] = useState(false);
  const [formFilled, setFormFilled] = useState(false);

  const [tag, setTag] = useState("");
  const [name, setName] = useState("");
  const [facebook, setFacebook] = useState("");
  const [instagram, setInstagram] = useState("");
  const [pinterest, setPinterest] = useState("");
  const [tiktok, setTiktok] = useState("");

  const creatorData = {
    tag,
    name,
    handlers: { facebook, instagram, pinterest, tiktok },
  };

  const stripePromise = useMemo(() => {
    if (!stripeConfig?.publishableKey) return null;
    return loadStripe(stripeConfig.publishableKey);
  }, [stripeConfig?.publishableKey]);

  const finalizeUpgrade = async (payload) => {
    const upgradedUser = await upgradeUserToCreator(payload).unwrap();
    dispatch(setCredentials({ ...upgradedUser }));
    toast.success("Membership payment successful!");
    setHasBecome(true);
    navigate("/profile/creator");
  };

  return (
    <section className="mx-auto w-full max-w-3xl">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        {!showForm ? (
          <div>
            <h1 className="mb-3 text-3xl font-semibold text-slate-900">Join Our Membership</h1>
            <p className="mb-3 text-sm text-slate-700">
              Become a member today and enjoy these benefits:
            </p>
            <ul className="mb-5 list-disc space-y-1 pl-5 text-sm text-slate-700">
              <li>Sell your activities to other professionals</li>
              <li>Receive the latest information in activity development</li>
              <li>Early access to new features</li>
            </ul>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700"
            >
              Sign up
            </button>
          </div>
        ) : !formFilled ? (
          <div className="space-y-4">
            <div>
              <h2 className="mb-1 text-2xl font-semibold text-slate-900">Become a Creator</h2>
              <p className="text-sm text-slate-600">
                Fill out the form below to set up your creator profile.
              </p>
            </div>
            {error && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">
                {error?.data?.message || "Something went wrong"}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Tag</label>
                <input
                  type="text"
                  placeholder="Enter your creator tag"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
                <input
                  type="text"
                  placeholder="Enter your creator name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                />
              </div>

              <div>
                <h3 className="mb-2 text-base font-semibold text-slate-900">Social Media Links (Optional)</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input
                    type="text"
                    placeholder="Facebook handle"
                    value={facebook}
                    onChange={(e) => setFacebook(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  />
                  <input
                    type="text"
                    placeholder="Instagram handle"
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  />
                  <input
                    type="text"
                    placeholder="Pinterest handle"
                    value={pinterest}
                    onChange={(e) => setPinterest(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  />
                  <input
                    type="text"
                    placeholder="TikTok handle"
                    value={tiktok}
                    onChange={(e) => setTiktok(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (!tag.trim() || !name.trim()) {
                    toast.error("Tag and name are required");
                    return;
                  }
                  setFormFilled(true);
                }}
                className="rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700"
              >
                Next
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">Confirm Your Creator Profile</h2>
            <p className="text-sm text-slate-700">
              Membership fee: <strong>{membershipFee.toFixed(2)} EUR</strong>
            </p>

            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <StripeUpgradeForm
                  creatorData={creatorData}
                  onUpgraded={finalizeUpgrade}
                  createUpgradeIntent={createUpgradeIntent}
                  confirmUpgradePayment={confirmUpgradePayment}
                />
              </Elements>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">
                Stripe is not configured for this environment.
              </div>
            )}

            {isLoading && <Loader />}
          </div>
        )}
      </div>
    </section>
  );
}

